import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { DataTable } from '@openedx/paragon';

// Format mark display for CS136
const formatCS136Mark = (mark) => {
  if (mark === 'X') {
    return 'Exempt';
  }
  if (mark === 'Complete' || parseFloat(mark) === 100) {
    return 'Complete';
  }
  return `${mark}/100`;
};

// Process CS136 data into sections
const processCS136Data = (data) => {
  const assignments = [];
  const styleMarks = [];
  const exams = [];
  const attendance = [];
  const quizMarks = [];
  let assignment0Incomplete = false;

  if (!Array.isArray(data)) {
    return { assignments, styleMarks, exams, attendance, quizMarks, assignment0Incomplete };
  }

  data.forEach((item) => {
    const name = item.assignmentName || item.assignment_name;
    const marks = item.marks;

    if (name === 'Assignment 0') {
      if (parseInt(marks, 10) === 0) {
        assignment0Incomplete = true;
      }
      assignments.push({
        name: name,
        display: 'Complete',
      });
    } else if (name?.includes('Assignment')) {
      if (name?.includes('Style')) {
        styleMarks.push({
          name: name,
          display: formatCS136Mark(marks),
        });
      } else {
        assignments.push({
          name: name,
          display: formatCS136Mark(marks),
        });
      }
    } else if (name?.includes('Classes Attended')) {
      attendance.push({
        name: name,
        display: marks,
      });
    } else if (name?.includes('Quiz')) {
      quizMarks.push({
        name: name,
        display: formatCS136Mark(marks),
      });
    } else {
      exams.push({
        name: name,
        display: formatCS136Mark(marks),
      });
    }
  });

  return {
    assignments,
    styleMarks,
    exams,
    attendance,
    quizMarks,
    assignment0Incomplete,
  };
};

// Helper function to render a DataTable for a section
const renderSectionTable = (sectionData, sectionTitle, intl) => {
  if (!sectionData || sectionData.length === 0) {
    return null;
  }

  const tableData = sectionData.map((item) => ({
    name: item.name,
    score: item.display,
  }));

  return (
    <div className="my-3" key={sectionTitle}>
      <DataTable
        data={tableData}
        itemCount={tableData.length}
        columns={[
          {
            Header: sectionTitle,
            accessor: 'name',
            headerClassName: 'h5 mb-0',
            cellClassName: 'mw-100',
          },
          {
            Header: 'Score',
            accessor: 'score',
            headerClassName: 'justify-content-end h5 mb-0',
            cellClassName: 'align-top text-right small',
          },
        ]}
      >
        <DataTable.Table />
      </DataTable>
    </div>
  );
};

const CS136Grades = ({ data, intl }) => {
  // Defensive check: ensure data is an array
  if (!Array.isArray(data)) {
    return null;
  }

  const processed = processCS136Data(data);

  return (
    <section className="text-dark-700">
      <h3 className="h4 mb-3">CS 136 Mark section</h3>
      {processed.assignment0Incomplete && (
        <div className="alert alert-warning" role="alert">
          <strong>Assignment 0 is incomplete! Please check your marmoset tests (including the private ones)!</strong>
        </div>
      )}

      {renderSectionTable(processed.assignments, 'Assignments', intl)}
      {renderSectionTable(processed.styleMarks, 'Style Marks', intl)}
      {renderSectionTable(processed.exams, 'Exams', intl)}
      {renderSectionTable(processed.attendance, 'Attendance', intl)}
      {renderSectionTable(processed.quizMarks, 'Quizzes', intl)}
    </section>
  );
};

CS136Grades.propTypes = {
  data: PropTypes.array,
  intl: intlShape,
};

CS136Grades.defaultProps = {
  data: [],
};

export default injectIntl(CS136Grades);
